'use client';

import { FC, memo, PropsWithChildren, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useWindowSizes } from '@/hooks/breakPoint';
import useForceUpdate from '@/hooks/forceUpdate';

const isSSR = typeof document === 'undefined';
const _document: null | Document = isSSR ? null : document;

interface IPortalRendererProps extends PropsWithChildren {
  id: string;
  shouldUseWindowAdaptor?: boolean;
  className?: string;
}

const useWindowAdaptor = (container: HTMLElement) => {
  useWindowSizes();
  const forceUpdate = useForceUpdate();
  useEffect(() => {
    if (!container) forceUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const PortalRenderer: FC<IPortalRendererProps> = memo(
  ({ id, shouldUseWindowAdaptor = true, children, className }) => {
    const container: HTMLElement = _document?.getElementById(id) as HTMLElement;

    const useIsomorphicWindowAdaptor: typeof useWindowAdaptor =
      shouldUseWindowAdaptor ? useWindowAdaptor : () => {};

    useIsomorphicWindowAdaptor(container);

    if (!container) {
      return null;
    }

    if (className) {
      container.className = className;
    }

    return createPortal(children, container);
  }
);
PortalRenderer.displayName = 'PortalRenderer';

export default PortalRenderer;
